import { shadowQueued, type Message, type OutboxOp, type Request, type RequestStatus, type RequestType } from '@dia/db';
import { fromRow, type ThreadMessage } from './thread';

/**
 * The one help thread (docs/GUEST-REDESIGN.md, "How Requests and Messages fit
 * together"), as pure functions.
 *
 * There is one thread per stay between the guest and the hosts. A plain message
 * is a bubble, exactly as Mensajes always drew it. A request ("Ask for
 * something") or a problem report ("Report a problem") is a card in the same
 * thread with its status on it, and the hosts' reply lands underneath it.
 *
 * Nothing new is stored. The thread is read from the two tables that already
 * exist, messages (thread_kind 'stay') and requests, plus whatever is still in
 * the outbox on this phone, and put in one order here.
 */

/** Which way the composer starts: a free message, a request, or a problem report. */
export type ComposerMode = 'message' | 'ask' | 'problem';

/**
 * The types "Ask for something" offers. "Something is broken" is not among
 * them: it has its own way in, "Report a problem", with room for a photo.
 */
export const ASK_TYPES: readonly RequestType[] = ['boat', 'provisioning', 'housekeeping', 'concierge', 'other'];

/**
 * A problem report is a maintenance request. The requests table has carried
 * that type since migration 006, the guest has always seen it as "Something is
 * broken", and the ops queue already labels it, so no new type is needed.
 */
export const PROBLEM_TYPE: RequestType = 'maintenance';

/** The request type a composer mode sends, given what the guest picked. */
export function requestTypeFor(mode: Exclude<ComposerMode, 'message'>, picked: RequestType): RequestType {
  if (mode === 'problem') return PROBLEM_TYPE;
  return ASK_TYPES.includes(picked) ? picked : 'other';
}

/**
 * The three guest facing words for a status, from the redesign doc: Received,
 * On it, Done. The ops side keeps its five values and its own words; only the
 * label a guest reads is folded. "Seen" reads as "Received" to a guest, since
 * what they want to know is whether it arrived. "Declined" keeps its own honest
 * word rather than being hidden inside another one.
 */
export type GuestStatusKey =
  | 'guest.request.status.new'
  | 'guest.request.status.in_progress'
  | 'guest.request.status.done'
  | 'guest.request.status.declined';

export function guestStatusKey(status: RequestStatus): GuestStatusKey {
  switch (status) {
    case 'new':
    case 'seen':
      return 'guest.request.status.new';
    case 'in_progress':
      return 'guest.request.status.in_progress';
    case 'done':
      return 'guest.request.status.done';
    case 'declined':
      return 'guest.request.status.declined';
  }
}

export type GuestStatusTone = 'info' | 'warn' | 'ok' | 'neutral';

export function guestStatusTone(status: RequestStatus): GuestStatusTone {
  switch (status) {
    case 'new':
    case 'seen':
      return 'info';
    case 'in_progress':
      return 'warn';
    case 'done':
      return 'ok';
    case 'declined':
      return 'neutral';
  }
}

export type MessageItem = ThreadMessage & {
  kind: 'message';
  /** A stay message may name the request it answers. Null for a plain message. */
  requestId: string | null;
};

export type RequestCard = {
  kind: 'request';
  /** The server id, or the client id while it is still on the phone. */
  id: string;
  clientId: string;
  type: RequestType;
  original: string;
  originalLang: 'es' | 'en';
  es: string | null;
  en: string | null;
  status: RequestStatus;
  dueAt: string | null;
  photoCount: number;
  createdAt: string;
  /** True while the request is on this phone and the island has not confirmed it. */
  pending: boolean;
};

/** One line of the thread. `under` names the request card a reply sits beneath. */
export type HelpThreadItem = (MessageItem | RequestCard) & { under: string | null };

function photoCount(paths: unknown): number {
  return Array.isArray(paths) ? paths.length : 0;
}

export function cardFromRow(row: Request): RequestCard {
  return {
    kind: 'request',
    id: row.id,
    clientId: row.client_id,
    type: row.type,
    original: row.text_original,
    originalLang: row.lang_original,
    es: row.text_es,
    en: row.text_en,
    status: row.status,
    dueAt: row.due_at,
    photoCount: photoCount(row.photo_paths),
    createdAt: row.created_at,
    pending: false,
  };
}

export function messageFromRow(row: Message): MessageItem {
  return { ...fromRow(row), kind: 'message', requestId: row.request_id };
}

type QueuedOp = Pick<OutboxOp, 'id' | 'table' | 'kind' | 'payload' | 'createdAt'>;

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function langOf(value: unknown): 'es' | 'en' {
  return value === 'es' ? 'es' : 'en';
}

/**
 * What is still in the outbox, as thread lines. Only inserts: an update (a
 * translation attached later) is not a new line. Anything the island already
 * has, matched by client_id, is left to the server copy so it shows once.
 */
export function queuedItems(
  ops: QueuedOp[],
  known: { messages: ReadonlySet<string>; requests: ReadonlySet<string> },
  stayId: string,
): Array<MessageItem | RequestCard> {
  const out: Array<MessageItem | RequestCard> = [];
  for (const op of ops) {
    if (op.kind !== 'insert') continue;
    const p = op.payload;
    const createdAt = new Date(op.createdAt).toISOString();
    if (op.table === 'messages') {
      if (known.messages.has(op.id)) continue;
      if (p.thread_kind !== 'stay' || p.stay_id !== stayId) continue;
      out.push({
        kind: 'message',
        id: op.id,
        clientId: op.id,
        from: 'guest',
        original: String(p.text_original ?? ''),
        originalLang: langOf(p.lang_original),
        es: str(p.text_es),
        en: str(p.text_en),
        createdAt,
        pending: true,
        requestId: str(p.request_id),
      });
    } else if (op.table === 'requests') {
      if (known.requests.has(op.id)) continue;
      if (p.stay_id !== stayId) continue;
      out.push({
        kind: 'request',
        id: op.id,
        clientId: op.id,
        type: (str(p.type) ?? 'other') as RequestType,
        original: String(p.text_original ?? ''),
        originalLang: langOf(p.lang_original),
        es: str(p.text_es),
        en: str(p.text_en),
        status: 'new',
        dueAt: str(p.due_at),
        photoCount: photoCount(p.photo_paths),
        createdAt,
        pending: true,
      });
    }
  }
  return out;
}

/**
 * One thread in reading order. Requests and plain messages sit in the order
 * they were written. A message that names a request in this thread is drawn
 * straight beneath that request's card, after any earlier reply to it, so the
 * answer is always under the question it answers. A message naming a request
 * that is not here (another stay's, or one not loaded yet) is simply a plain
 * message in time order, never lost.
 */
export function buildHelpThread(items: Array<MessageItem | RequestCard>): HelpThreadItem[] {
  const cards = new Set(items.filter((i): i is RequestCard => i.kind === 'request').map((c) => c.id));
  const byTime = (a: { createdAt: string }, b: { createdAt: string }) => a.createdAt.localeCompare(b.createdAt);

  const replies = new Map<string, MessageItem[]>();
  const top: Array<MessageItem | RequestCard> = [];
  for (const item of items) {
    if (item.kind === 'message' && item.requestId && cards.has(item.requestId)) {
      const list = replies.get(item.requestId) ?? [];
      list.push(item);
      replies.set(item.requestId, list);
    } else {
      top.push(item);
    }
  }

  const out: HelpThreadItem[] = [];
  for (const item of [...top].sort(byTime)) {
    out.push({ ...item, under: null });
    if (item.kind === 'request') {
      for (const reply of (replies.get(item.id) ?? []).sort(byTime)) {
        out.push({ ...reply, under: item.id });
      }
    }
  }
  return out;
}

/**
 * Which way the composer opens. The sheet passes it directly; the two old
 * addresses keep meaning what they always meant: /mensajes is a message,
 * /pedidos is a request, and /pedidos?tipo=problema is a problem report.
 */
export function modeForRoute(pathname: string, problem: boolean): ComposerMode {
  if (pathname.startsWith('/mensajes')) return 'message';
  return problem ? 'problem' : 'ask';
}

/**
 * A line this guest wrote that reached the island without its Spanish.
 *
 * The phone asks for a translation before it sends, for a couple of seconds at
 * most, and never lets that hold the write. So a request or a message written
 * with no signal, or on a slow one, lands in English only, and the crew read
 * Spanish. Once the island has the row, the phone that wrote it attaches the
 * Spanish through the outbox (migration 018 for a message, 022 for a request).
 *
 * The server row itself is the reminder: while text_es is empty the line is
 * still owed its translation, so this survives the app being closed, the phone
 * restarting, and any number of dropouts in between, without keeping a list.
 */
export type LateShadowJob = { table: 'messages' | 'requests'; clientId: string; text: string };

export function lateShadowJobs(
  rows: { messages: readonly Message[]; requests: readonly Request[] },
  guestId: string,
  queued: ReadonlyArray<Pick<OutboxOp, 'table' | 'kind' | 'match'>>,
): LateShadowJob[] {
  const jobs: LateShadowJob[] = [];
  for (const r of rows.requests) {
    if (!r.created_by_guest || r.lang_original !== 'en' || r.text_es) continue;
    if (shadowQueued(queued, 'requests', r.client_id)) continue;
    jobs.push({ table: 'requests', clientId: r.client_id, text: r.text_original });
  }
  for (const m of rows.messages) {
    // Only the sender may attach a message's shadow (migration 018).
    if (m.thread_kind !== 'stay' || m.author_kind !== 'guest' || m.author_guest_id !== guestId) continue;
    if (m.lang_original !== 'en' || m.text_es) continue;
    if (shadowQueued(queued, 'messages', m.client_id)) continue;
    jobs.push({ table: 'messages', clientId: m.client_id, text: m.text_original });
  }
  return jobs;
}
